//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useContext, useEffect } from 'react';
import {
  TargetSelectorContext, type TargetSelectorOption
} from '@/contexts/TargetSelectorContext';

/** Reads the current registration; used by the header's selector. */
export function useTargetSelector() {
  const context = useContext(TargetSelectorContext);
  if (context === undefined) {
    throw new Error(
      'useTargetSelector must be used within a TargetSelectorProvider');
  }
  return context;
}

/**
 * Lends the header's dataset selector this page's own entries for as long as
 * the page is mounted.  Memoize `options` and `onSelect` in the caller: they
 * are the effect's dependencies, so a fresh identity every render would
 * re-register in a loop.
 */
export function useRegisterTargetSelector(
  options: TargetSelectorOption[],
  value: string | null,
  onSelect: (value: string) => void
) {
  const { register } = useTargetSelector();

  useEffect(() => {
    register({ options, value, onSelect });
    return () => register(null);
  }, [register, options, value, onSelect]);
}
